import { CaseLists } from '@/components/crime/CaseLists';
import { CrimeResult } from '@/components/crime/CrimeResult';
import { CrimeSearchBar } from '@/components/crime/CrimeSearchBar';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuth } from '@/context/AuthContext';
import { IResponse } from '@/types/crime';
import { css } from '@emotion/react';
import { CircularProgress } from '@mui/material';
import axios, { AxiosResponse } from 'axios';
import { useRouter } from 'next/router';
import React, { MouseEvent, ReactElement, useEffect, useState } from 'react';

export default function CrimePage() {
  const router = useRouter();
  const { user } = useAuth();

  const [queryText, setQueryText] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string | undefined>('');
  const [data, setData] = useState<IResponse>({
    id: '',
    input: '',
    output: [],
  });

  useEffect(() => {
    setEmail(user?.email);
  }, [user]);

  useEffect(() => {
    let routerId: string | string[] | undefined = router.query.id;

    if (routerId === undefined) {
      routerId = '';
    } else if (Array.isArray(routerId)) {
      routerId = '';
    }
    const id = routerId;
    getCrimeData(id);
  }, [router.query]);

  const getCrimeData = async (id: string) => {
    if (!id) return;
    const baseUrl = process.env.NEXT_PUBLIC_REST_API_URI;
    try {
      setIsLoading(true);
      const res: AxiosResponse = await axios.get(`${baseUrl}/api/crime/${id}`);
      const data: IResponse = res.data;
      setData(data);
      setQueryText(data.input);
      setIsLoading(false);
    } catch (error) {
      console.log('error', error);
    }
  };

  const postQuery = async (query: string) => {
    if (!query) return;
    const baseUrl = process.env.NEXT_PUBLIC_REST_API_URI;
    try {
      const payload = { q: query };
      const res: AxiosResponse = await axios.post(`${baseUrl}/api/crime`, payload);
      const id: IResponse = res.data.id;
      return id;
    } catch (error) {
      console.log('error', error);
    }
  };

  const onClickSearch = (ev: MouseEvent<HTMLButtonElement>) => {
    if (queryText) {
      onSearch(queryText);
    }
  };

  const onSearch = async (queryText: string) => {
    const id = await postQuery(queryText);
    const sliceQuery = queryText.slice(0, 50);
    const queryString = `/crimesearch?id=${id}#${sliceQuery}`;
    router.push(queryString, undefined, { shallow: true });
  };

  const placeholder =
    '[예시] 2021년 12월 25일 강원 인제군 출렁다리 앞 도로에서 술에 취한 상태로 화물차를 운전하였다.';

  const mobileCss = `width: 100%; font-size:16px;`;

  return (
    <>
      {isLoading ? (
        <section
          css={css`
            max-width: 80%;
            min-height: calc(100vh - 64px);
            display: flex;
            flex-flow: row nowrap;
            justify-content: center;
            align-items: center;
            margin: auto;
            @media (max-width: 992px) {
              max-width: 90%;
            }
          `}
        >
          <CircularProgress />
        </section>
      ) : (
        <section
          css={css`
            max-width: 80%;
            min-height: calc(100vh - 64px);
            display: flex;
            flex-flow: column nowrap;
            justify-content: flex-start;
            align-items: center;
            margin: 2rem auto;
            gap: 2rem;
            @media (max-width: 992px) {
              max-width: 90%;
            }
          `}
        >
          <div
            css={css`
              font-size: 22px;
              font-weight: 700;
              color: #002366;
              justify-items: flex-start;
              border-bottom: 2px solid #002366;
              padding-bottom: 1rem;
              width: 80%;
              @media (max-width: 992px) {
                ${mobileCss}
                font-size:18px;
              }
            `}
          >
            {`무죄 판결문을 찾아주는 리걸엔진`}
          </div>
          <CrimeSearchBar
            placeholder={placeholder}
            queryText={queryText}
            setQueryText={setQueryText}
          />
          <button
            css={css`
              width: 19rem;
              height: 3rem;
              color: #ffffff;
              background-color: #002366;
              border-radius: 5px;
              font-size: 20px;
              font-weight: 700;
              cursor: pointer;
              @media (max-width: 992px) {
                ${mobileCss}
              }
            `}
            onClick={onClickSearch}
          >
            {`검색하기`}
          </button>
          <CrimeResult userEmail={email} data={data} />
          <CaseLists data={data} />
        </section>
      )}
    </>
  );
}

CrimePage.getLayout = (page: ReactElement) => {
  return <MainLayout>{page}</MainLayout>;
};
